import { Injectable } from '@angular/core';
import { formatNumber } from '../../../../common/shared/helpers/number';
import { NamedPointInterface, SideInterface } from './custom-shape.constants';

@Injectable()
export class DrawShapeService {
  /**
   * Get the mouse coordinates relative to the SVG element
   * @param event Mouse Event
   */
  getMousePosition(event: MouseEvent) {
    return {
      x: event.offsetX,
      y: event.offsetY,
    };
  }

  angleFromCoordinates(x: number, y: number) {
    const rad = Math.atan2(y, x);
    const pi = Math.PI;
    let angle = Math.round(rad * (180 / pi));
    if (angle < 0) {
      angle = angle + 360;
    }
    return angle;
  }

  /**
   * Translate current polygon into a new one
   * @param polygon An array of polygon coordinates
   * @param angle The angle created by the polygon and the current mouse position
   * @param distance The distance from the polygon to the mouse position
   * @returns
   */
  polygonTranslate(polygon: number[][], angle: number, distance: number) {
    const p = [];
    for (let i = 0, l = polygon.length; i < l; i++) {
      const r = (angle / 180) * Math.PI;
      p[i] = [polygon[i][0] + distance * Math.cos(r), polygon[i][1] + distance * Math.sin(r)];
    }
    return p;
  }

  getMostLeftPoint(polygon: number[][]): number[] {
    return polygon.reduce((minPoint, point) => {
      if (point[0] < minPoint[0]) {
        return point;
      }
      return minPoint;
    }, polygon[0]);
  }
  getHighestPoint(polygon: number[][]): number[] {
    return polygon.reduce((minPoint, point) => {
      if (point[1] > minPoint[1]) {
        return point;
      }
      return minPoint;
    }, polygon[0]);
  }

  movePointsForCalculation(polygon: number[][], shapeHeight: number): number[][] {
    const leftPoint = this.getMostLeftPoint(polygon);
    const highestPoint = this.getHighestPoint(polygon);
    const xDistance = leftPoint[0];
    const yDistance = shapeHeight - highestPoint[1];
    return polygon.map((point) => [point[0] - xDistance, point[1] + yDistance]);
  }

  getRoundedTileHeight(originalHeight: number): number {
    let height = formatNumber(Math.round((originalHeight - 0.15) / 0.35) * 0.35 + 0.15);
    if (height < originalHeight - 0.05) {
      height = Math.round((originalHeight - 0.15) / 0.35) * 0.35 + 0.15;
    }

    return formatNumber(height);
  }

  getNamedPoints(points: number[][]): NamedPointInterface[] {
    return points.map(([x, y], index) => ({
      x,
      y,
      name: this.getPointName(index),
    }));
  }

  getPointName(index: number): string {
    return String.fromCharCode(65 + index);
  }

  getSides(points: number[][]): SideInterface[] {
    return points.map((point, index) => {
      if (index === points.length - 1) {
        return {
          name: this.getPointName(index) + this.getPointName(0),
          length: this.getDistanceBetweenPoints(point[0], point[1], points[0][0], points[0][1]),
        };
      }
      return {
        name: this.getPointName(index) + this.getPointName(index + 1),
        length: this.getDistanceBetweenPoints(point[0], point[1], points[index + 1][0], points[index + 1][1]),
      };
    });
  }

  getDistanceBetweenPoints(x1: number, y1: number, x2: number, y2: number): number {
    const x = x2 - x1;
    const y = y2 - y1;
    return Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
  }
}
